import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkle, Bell, ChartLineUp, Heart } from "@phosphor-icons/react";
import { Section, EmptyState, SEVERITY_HEX } from "./_shared";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, CartesianGrid } from "recharts";
import Stakeholders from "./Stakeholders";
import { AIAssistant } from "./_ai";

/* Static recharts axis / cursor configs — extracted so they're not allocated
   every render. */
const AXIS_TICK = { fontSize: 11, fill: "#52525B" };
const TOOLTIP_CURSOR = { fill: "rgba(0,0,0,0.04)" };
const TOOLTIP_STYLE = { background: "#09090B", border: "none", color: "#fff", fontSize: 12 };

export default function InsightsLens({ projectId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError] = useState(null);

  const load = async ({ regenerate = false } = {}) => {
    if (regenerate) setRegenerating(true);
    else setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(
        `/projects/${projectId}/insights${regenerate ? "?regenerate=true" : ""}`,
      );
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load insights");
    } finally {
      setLoading(false);
      setRegenerating(false);
    }
  };

  useEffect(() => { load(); }, [projectId]);

  const sev = data?.stats?.risks_by_severity || {};
  const urg = data?.stats?.issues_by_urgency || {};

  // Memoise chart data so Recharts doesn't re-key Bar cells on every render.
  const riskData = useMemo(() => [
    { name: "Critical", value: sev.critical || 0, fill: SEVERITY_HEX.critical },
    { name: "High",     value: sev.high || 0,     fill: SEVERITY_HEX.high },
    { name: "Medium",   value: sev.medium || 0,   fill: SEVERITY_HEX.medium },
    { name: "Low",      value: sev.low || 0,      fill: SEVERITY_HEX.low },
  ], [sev.critical, sev.high, sev.medium, sev.low]);
  const issueData = useMemo(() => [
    { name: "Critical", value: urg.critical || 0, fill: SEVERITY_HEX.critical },
    { name: "High",     value: urg.high || 0,     fill: SEVERITY_HEX.high },
    { name: "Medium",   value: urg.medium || 0,   fill: SEVERITY_HEX.medium },
    { name: "Low",      value: urg.low || 0,      fill: SEVERITY_HEX.low },
  ], [urg.critical, urg.high, urg.medium, urg.low]);

  const health = data?.ai?.health_score ?? 0;

  return (
    <div data-testid="insights-lens">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-zinc-600">
          Executive synthesis across all five lenses.{" "}
          {data?.cached && (
            <span className="text-xs text-zinc-400">
              · Cached from {new Date(data.generated_at).toLocaleString()}
              {data.stale ? " · Showing last successful result" : ""}
            </span>
          )}
        </div>
        <button onClick={() => load({ regenerate: true })} disabled={loading || regenerating}
          className="inline-flex items-center gap-2 h-10 px-4 bg-zinc-950 text-white text-xs font-bold hover:bg-zinc-800 disabled:opacity-50"
          data-testid="regenerate-insights-button">
          <Sparkle size={14} weight="fill" />
          {regenerating ? "Regenerating…" : "Regenerate insights"}
        </button>
      </div>

      {loading && !data && <div className="text-zinc-500 text-sm">Loading…</div>}
      {error && <div className="bg-white border border-[#FF3B30] p-5 text-sm text-[#FF3B30]" data-testid="insights-error">{error}</div>}

      {data && (
        <>
          {/* Top stats */}
          <div className="grid lg:grid-cols-[1fr_2fr] gap-px bg-zinc-200 border border-zinc-200 mb-6">
            <div className="bg-white p-6">
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500 flex items-center gap-2"><Heart size={12} weight="fill" /> Project health</div>
              <div className="flex items-end gap-2 mt-3">
                <div className="font-display font-black text-6xl" style={{ color: health >= 70 ? "#16A34A" : health >= 40 ? "#FF9500" : "#FF3B30" }} data-testid="health-score">{health}</div>
                <div className="text-zinc-400 pb-2">/100</div>
              </div>
              <div className="w-full h-2 bg-zinc-100 mt-3">
                <div className="h-2" style={{ width: `${health}%`, background: health >= 70 ? "#16A34A" : health >= 40 ? "#FF9500" : "#FF3B30" }} />
              </div>
            </div>
            <div className="bg-white p-6">
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500 flex items-center gap-2"><ChartLineUp size={12} weight="fill" /> Executive headline</div>
              <p className="font-display font-bold text-2xl mt-2 leading-snug" data-testid="insights-headline">{data.ai?.headline}</p>
              <div className="mt-4 grid grid-cols-4 gap-3 text-xs">
                <div><div className="text-zinc-500 text-[10px] uppercase tracking-[0.2em] font-bold">Assumptions</div><div className="font-mono mt-1">{data.stats?.assumptions_total}</div></div>
                <div><div className="text-zinc-500 text-[10px] uppercase tracking-[0.2em] font-bold">Risks</div><div className="font-mono mt-1">{data.stats?.risks_total}</div></div>
                <div><div className="text-zinc-500 text-[10px] uppercase tracking-[0.2em] font-bold">Issues</div><div className="font-mono mt-1">{data.stats?.issues_total} <span className="text-zinc-400">({data.stats?.issues_open} open)</span></div></div>
                <div><div className="text-zinc-500 text-[10px] uppercase tracking-[0.2em] font-bold">Solutions</div><div className="font-mono mt-1">{data.stats?.solutions_total}</div></div>
              </div>
            </div>
          </div>

          {/* Charts */}
          <div className="grid lg:grid-cols-2 gap-6 mb-6">
            <Section eyebrow="Distribution" title="Risks by severity">
              <div className="h-64">
                <ResponsiveContainer>
                  <BarChart data={riskData}>
                    <CartesianGrid stroke="#E4E4E7" strokeDasharray="0" vertical={false} />
                    <XAxis dataKey="name" tick={AXIS_TICK} />
                    <YAxis allowDecimals={false} tick={AXIS_TICK} />
                    <Tooltip cursor={TOOLTIP_CURSOR} contentStyle={TOOLTIP_STYLE} />
                    <Bar dataKey="value">
                      {riskData.map((entry) => <Cell key={entry.name} fill={entry.fill} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Section>
            <Section eyebrow="Distribution" title="Issues by urgency">
              <div className="h-64">
                <ResponsiveContainer>
                  <BarChart data={issueData}>
                    <CartesianGrid stroke="#E4E4E7" strokeDasharray="0" vertical={false} />
                    <XAxis dataKey="name" tick={AXIS_TICK} />
                    <YAxis allowDecimals={false} tick={AXIS_TICK} />
                    <Tooltip cursor={TOOLTIP_CURSOR} contentStyle={TOOLTIP_STYLE} />
                    <Bar dataKey="value">
                      {issueData.map((entry) => <Cell key={entry.name} fill={entry.fill} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Section>
          </div>

          {/* Alerts + Recs */}
          <div className="grid lg:grid-cols-2 gap-6">
            <Section eyebrow="Alerts" title={<><Bell className="inline" size={18} weight="duotone" /> Active alerts</>}>
              {(data.ai?.alerts || []).length === 0 ? (
                <EmptyState title="No alerts" hint="Project is currently within thresholds." testid="empty-alerts" />
              ) : (
                <ul className="space-y-2" data-testid="alerts-list">
                  {(data.ai.alerts || []).map((a, i) => (
                    <li key={`alert-${i}-${a.slice(0, 24)}`} className="p-3 border border-zinc-200 bg-zinc-50 text-sm text-zinc-800 font-mono">{a}</li>
                  ))}
                </ul>
              )}
            </Section>
            <Section eyebrow="Actions" title="AI recommendations">
              {(data.ai?.recommendations || []).length === 0 ? (
                <EmptyState title="No recommendations" hint="Add more data and regenerate." testid="empty-recs" />
              ) : (
                <ol className="space-y-2 list-decimal list-inside text-sm text-zinc-800" data-testid="recommendations-list">
                  {(data.ai.recommendations || []).map((r, i) => (
                    <li key={`rec-${i}-${r.slice(0, 24)}`} className="p-3 border border-zinc-200">{r}</li>
                  ))}
                </ol>
              )}
            </Section>
          </div>

          {/* Top priorities with concrete resolutions */}
          {(data.ai?.top_priorities || []).length > 0 && (
            <div className="mt-6">
              <Section eyebrow="Resolutions" title="Top priorities · what to do next">
                <div className="space-y-3" data-testid="top-priorities-list">
                  {(data.ai.top_priorities || []).map((p, i) => (
                    <div
                      key={`prio-${i}-${(p.title || "").slice(0, 24)}`}
                      className="border-l-4 border-[#0020F5] bg-white p-4 shadow-sm"
                      data-testid={`top-priority-${i}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="font-display font-bold text-base text-zinc-950">{p.title || "Untitled priority"}</div>
                        <span className="text-[10px] font-bold tracking-[0.15em] uppercase bg-[#0020F5]/10 text-[#0020F5] px-2 py-1 whitespace-nowrap">{p.deadline_hint || "This week"}</span>
                      </div>
                      <p className="mt-2 text-sm text-zinc-700 leading-relaxed">{p.resolution || ""}</p>
                      <div className="mt-3 text-[11px] font-mono text-zinc-500">
                        Owner: <span className="font-bold text-zinc-900">{p.owner_role || "Project Manager"}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </Section>
            </div>
          )}
        </>
      )}

      <Stakeholders projectId={projectId} />
      <AIAssistant projectId={projectId} lens="insights" label="Insights" />
    </div>
  );
}
